import { Button } from '@mantine/core';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Breadcrumb } from '@/components/Ui/Breadcrumb';
import { CopyButton } from '@/components/Ui/CopyButton';
import { DocumentTitle } from '@/components/Ui/DocumentTitle';
import { InlineTextEditor } from '@/components/Ui/InlineTextEditor';
import { useLanguageAwareRouting } from '@/hooks/useLanguageAwareRouting';
import { submissionUrl } from '@/lib/form-site';
import { shortDate } from '@/lib/relative-time';
import classes from './FormOverview.module.css';
import type { SectionProps } from './types';

interface Props extends Pick<SectionProps, 'form' | 'save' | 'errors' | 'data'> {
  chatOpen: boolean;
  onToggleChat: () => void;
}

export function OverviewHeader({ form, save, errors, data, chatOpen, onToggleChat }: Props) {
  const { t, i18n } = useTranslation();
  const { createLanguageURL } = useLanguageAwareRouting();
  const [renaming, setRenaming] = useState(false);
  const url = submissionUrl(form.id);
  const kept = data.summary?.messages_total;

  const rename = async (next: string) => {
    const title = next.trim();
    if (!title) throw new Error('empty');
    if (title === form.title) {
      setRenaming(false);
      return;
    }
    const ok = await save({ title }, t('form.header.renamed', { title }));
    if (!ok) throw new Error('rejected');
    setRenaming(false);
  };

  return (
    <div className={classes.headerWrap}>
      <DocumentTitle title={form.title} />
      <Breadcrumb
        items={[{ label: t('forms.pageTitle'), to: '/account/forms' }, { label: form.title }]}
      />
      <header className={classes.header}>
        <div className={classes.titles}>
          {renaming ? (
            <InlineTextEditor
              value={form.title}
              label={t('form.header.nameLabel')}
              error={errors.title}
              onSave={rename}
              onCancel={() => setRenaming(false)}
            />
          ) : (
            <div className={classes.titleLine}>
              <h1 className={classes.title}>{form.title}</h1>
              <Button variant="subtle" size="xs" onClick={() => setRenaming(true)}>
                {t('form.header.rename')}
              </Button>
            </div>
          )}
          <div className={classes.urlLine}>
            <span className={classes.sub}>{t('form.header.submissionUrl')}</span>
            <code className={classes.mono}>{url}</code>
            <CopyButton text={url} />
          </div>
          <div className={classes.facts}>
            <span>{t(`form.header.encryption.${form.encryption_mode}`, { defaultValue: '' })}</span>
            <span>
              {t('form.header.created', { date: shortDate(form.created_at, i18n.language) })}
            </span>
            {typeof kept === 'number' && <span>{t('form.header.kept', { count: kept })}</span>}
          </div>
        </div>
        <div className={classes.headerActions}>
          <Button onClick={onToggleChat} aria-pressed={chatOpen}>
            {t('form.header.ask')}
          </Button>
          <Button
            component={Link}
            to={createLanguageURL(`/account/messages?form=${encodeURIComponent(form.id)}`)}
          >
            {t('form.header.messages')}
          </Button>
        </div>
      </header>
    </div>
  );
}
